import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, ShieldOff, Shield, KeyRound, Check, Copy as CopyIcon } from "lucide-react";
import { BackButton } from "@/components/BackButton";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("rep");
  const [adminPassword, setAdminPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [issuedCred, setIssuedCred] = useState(null); // {email, temp_password}

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [reassignTo, setReassignTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activeReps = useMemo(
    () => users.filter((u) => u.role === "rep" && u.active),
    [users]
  );

  const createRep = async (e) => {
    e.preventDefault();
    if (newRole === "admin" && !adminPassword) {
      toast.error("Confirm your admin password to create another admin.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: newRole,
      };
      if (newRole === "admin") payload.admin_password = adminPassword;
      const { data } = await api.post("/users", payload);
      const label = newRole === "admin" ? "Admin" : newRole === "office_manager" ? "Office manager" : "Rep";
      toast.success(`${label} created: ${data.user.email}`);
      setIssuedCred({ email: data.user.email, temp_password: data.temp_password });
      setNewEmail("");
      setNewName("");
      setNewRole("rep");
      setAdminPassword("");
      setShowAdd(false);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const doDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      const { data } = await api.post(`/users/${deactivateTarget.id}/deactivate`, {
        reassign_to: reassignTo || null,
      });
      toast.success(
        `Deactivated. Reassigned ${data.reassigned_leads} lead${
          data.reassigned_leads === 1 ? "" : "s"
        }.`
      );
      setDeactivateTarget(null);
      setReassignTo("");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setIssuedCred({ email: u.email, temp_password: data.temp_password });
      toast.success(`Temporary password issued for ${u.email}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="users-page">
      <div className="px-8 py-6 max-w-5xl">
        <div className="mb-3">
          <BackButton />
        </div>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink mt-1">Team</h1>
          </div>
          <button
            onClick={() => setShowAdd(!showAdd)}
            data-testid="toggle-add-rep-btn"
            className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors active:scale-[0.98]"
          >
            <UserPlus size={14} />
            {showAdd ? "Close" : "Add member"}
          </button>
        </div>

        {showAdd && (
          <form
            onSubmit={createRep}
            className="mb-6 border border-line rounded-sm bg-bg-surface p-5"
            data-testid="add-rep-form"
          >
            <h2 className="text-base font-medium text-ink mb-3">Add a team member</h2>
            <div className="grid md:grid-cols-3 gap-3">
              <label className="block">
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
                  Full name
                </div>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                  data-testid="new-rep-name-input"
                  className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
                />
              </label>
              <label className="block">
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
                  Email
                </div>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  data-testid="new-rep-email-input"
                  className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors font-mono"
                />
              </label>
              <label className="block">
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
                  Role
                </div>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  data-testid="new-rep-role-select"
                  className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
                >
                  <option value="rep">Sales rep</option>
                  <option value="office_manager">Office manager</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </label>
            </div>

            {newRole === "admin" && (
              <div className="mt-4 border border-star/40 bg-star/5 rounded-sm p-4" data-testid="admin-confirm-block">
                <div className="flex items-baseline gap-2 mb-2">
                  <Shield size={12} className="text-star relative top-0.5" />
                  <div className="text-[11px] font-mono uppercase tracking-widest text-star">
                    Admin creation — high privilege
                  </div>
                </div>
                <p className="text-xs text-ink-secondary leading-snug mb-3">
                  New admins can create/deactivate users, view all revenue, change Stripe keys, and read the audit log.
                  Re-confirm your own password to authorize.
                </p>
                <label className="block">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
                    Your admin password
                  </div>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required={newRole === "admin"}
                    autoComplete="current-password"
                    data-testid="admin-confirm-password"
                    className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors font-mono"
                  />
                </label>
              </div>
            )}
            <div className="mt-4">
              <button
                type="submit"
                disabled={creating}
                data-testid="create-rep-btn"
                className="bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60 active:scale-[0.98]"
              >
                {creating ? "Creating…" : "Create member"}
              </button>
              <span className="text-[11px] text-ink-muted ml-3">
                A temporary password will be generated and shown once.
              </span>
            </div>
          </form>
        )}

        {issuedCred && (
          <CredentialBanner
            email={issuedCred.email}
            tempPassword={issuedCred.temp_password}
            onDismiss={() => setIssuedCred(null)}
          />
        )}

        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-14 border border-line rounded-sm animate-pulse bg-bg-surface" />
            ))}
          </div>
        ) : (
          <div className="border border-line rounded-sm bg-bg-surface" data-testid="users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-b border-line last:border-b-0" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3 text-ink">{u.name}</td>
                      <td className="px-4 py-3 text-ink-secondary font-mono text-[13px]">{u.email}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                            u.role === "admin"
                              ? "text-cyan border-cyan"
                              : u.role === "office_manager"
                              ? "text-star border-star"
                              : "text-ink-secondary border-line"
                          }`}
                        >
                          {u.role === "office_manager" ? "office mgr" : u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.active ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] text-ink font-mono uppercase tracking-wider">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-muted font-mono uppercase tracking-wider">
                            <span className="h-1.5 w-1.5 rounded-full bg-ink-muted" />
                            Deactivated
                          </span>
                        )}
                        {u.must_change_password && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-star">
                            reset pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          {u.role !== "admin" && (
                            <button
                              onClick={() => resetPassword(u)}
                              data-testid={`reset-password-btn-${u.id}`}
                              className="flex items-center gap-1 text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
                            >
                              <KeyRound size={11} />
                              Reset password
                            </button>
                          )}
                          {u.role !== "admin" && !isSelf && (
                            u.active ? (
                              <button
                                onClick={() => {
                                  setDeactivateTarget(u);
                                  setReassignTo("");
                                }}
                                data-testid={`deactivate-btn-${u.id}`}
                                className="flex items-center gap-1 text-xs text-destructive hover:text-white border border-line hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
                              >
                                <ShieldOff size={11} />
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => reactivate(u)}
                                data-testid={`reactivate-btn-${u.id}`}
                                className="flex items-center gap-1 text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
                              >
                                <Shield size={11} />
                                Reactivate
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {deactivateTarget && (
          <DeactivateModal
            user={deactivateTarget}
            activeReps={activeReps.filter((r) => r.id !== deactivateTarget.id)}
            reassignTo={reassignTo}
            setReassignTo={setReassignTo}
            onConfirm={doDeactivate}
            onCancel={() => {
              setDeactivateTarget(null);
              setReassignTo("");
            }}
          />
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted font-medium ${className}`}
    >
      {children}
    </th>
  );
}

function CredentialBanner({ email, tempPassword, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      className="mb-6 border rounded-sm p-4"
      style={{
        backgroundColor: "rgba(244, 184, 96, 0.06)",
        borderColor: "rgba(244, 184, 96, 0.3)",
      }}
      data-testid="credential-banner"
    >
      <div className="text-[11px] font-mono uppercase tracking-widest text-star mb-2">
        One-time temporary password
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-mono text-sm text-ink">
          <span className="text-ink-muted">{email}</span>
          <span className="mx-2">·</span>
          <span data-testid="temp-password-value">{tempPassword}</span>
        </div>
        <button
          onClick={copy}
          data-testid="copy-temp-password-btn"
          className="flex items-center gap-1 text-xs border border-line text-ink hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
        >
          {copied ? <Check size={11} className="text-cyan" /> : <CopyIcon size={11} />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          onClick={onDismiss}
          className="text-xs text-ink-muted hover:text-ink ml-auto transition-colors"
        >
          Dismiss
        </button>
      </div>
      <div className="text-[11px] text-ink-muted mt-2">
        This value is shown once. Copy it now — the user will be forced to change
        it on first login.
      </div>
    </div>
  );
}

function DeactivateModal({ user, activeReps, reassignTo, setReassignTo, onConfirm, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-start p-6 bg-black/60"
      role="dialog"
      aria-modal="true"
      data-testid="deactivate-modal"
    >
      <div className="mx-auto mt-24 w-full max-w-lg border border-line rounded-sm bg-bg-surface p-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Deactivate rep
        </div>
        <h2 className="text-lg font-medium text-ink mt-1">
          {user.name} <span className="text-ink-muted font-normal">({user.email})</span>
        </h2>
        <p className="text-sm text-ink-secondary mt-3 leading-relaxed">
          Their leads and activity history will stay in the system. Choose a rep
          to inherit their assigned leads (or leave unassigned for later).
        </p>
        <label className="block mt-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
            Reassign leads to
          </div>
          <select
            value={reassignTo}
            onChange={(e) => setReassignTo(e.target.value)}
            data-testid="reassign-to-select"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
          >
            <option value="">Leave unassigned</option>
            {activeReps.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={onConfirm}
            data-testid="confirm-deactivate-btn"
            className="bg-destructive text-bg font-medium py-2 px-4 rounded-sm hover:opacity-90 transition-opacity"
            style={{ color: "#0E1315" }}
          >
            Deactivate
          </button>
          <button
            onClick={onCancel}
            className="text-ink-secondary text-sm hover:text-ink transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
